import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Alert,
  AlertDescription,
  Input,
  Label,
} from "@/components/ui";
import { Download, RefreshCw, AlertCircle } from "lucide-react";
import reportsService from "@/services/reportsService";
import { SubsidiaryFilter } from "../filters";

/**
 * Balance Sheet Component
 *
 * Displays assets, liabilities, and equity with balance verification.
 *
 * Requirements: 7.1-7.9
 */
const BalanceSheet = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [statement, setStatement] = useState(null);
  const [filters, setFilters] = useState({
    subsidiary_id: "",
    as_of_date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    if (filters.as_of_date) {
      loadStatement();
    }
  }, []);

  const loadStatement = async () => {
    if (!filters.as_of_date) {
      return;
    }

    try {
      setLoading(true);
      const response = await reportsService.getBalanceSheet(filters);

      if (response?.data?.success) {
        setStatement(response.data.data);
      }
    } catch (error) {
      console.error("Failed to load balance sheet:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    loadStatement();
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const exportData = {
        reportData: statement,
        reportType: "balance_sheet",
        reportTitle: `Balance Sheet as of ${filters.as_of_date}`,
        format,
        filters,
        auditTrail: "summary",
      };

      const response =
        format === "pdf"
          ? await reportsService.exportToPDF(exportData)
          : await reportsService.exportToExcel(exportData);

      if (response?.data?.success) {
        const downloadResponse = await reportsService.downloadExport(
          response.data.data.history_id,
        );
        const url = window.URL.createObjectURL(
          new Blob([downloadResponse.data]),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", response.data.data.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const isBalanced = () => {
    if (!statement) return true;
    const diff = Math.abs(
      statement.assets?.total -
        (statement.liabilities?.total + statement.equity?.total),
    );
    return diff < 0.01; // Allow for rounding errors
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Balance Sheet</h1>
          <p className="text-gray-600 mt-1">
            Statement of financial position showing assets, liabilities, and
            equity
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport("pdf")}
            disabled={exporting || !statement}
          >
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport("excel")}
            disabled={exporting || !statement}
          >
            <Download className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SubsidiaryFilter
              value={filters.subsidiary_id}
              onChange={(value) =>
                setFilters({ ...filters, subsidiary_id: value })
              }
            />
            <div className="space-y-2">
              <Label>As of Date</Label>
              <Input
                type="date"
                value={filters.as_of_date}
                onChange={(e) =>
                  setFilters({ ...filters, as_of_date: e.target.value })
                }
              />
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={handleApplyFilters} disabled={loading}>
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                "Generate Statement"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Balance Warning */}
      {statement && !isBalanced() && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Warning: Balance sheet does not balance. Assets do not equal
            Liabilities + Equity. Please review your transactions.
          </AlertDescription>
        </Alert>
      )}

      {/* Statement */}
      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Generating statement...</p>
          </CardContent>
        </Card>
      ) : statement ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Assets */}
          <Card>
            <CardHeader>
              <CardTitle className="text-green-700">ASSETS</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableBody>
                  {Object.entries(statement.assets?.accounts || {}).map(
                    ([subtype, group]) => (
                      <React.Fragment key={subtype}>
                        <TableRow className="bg-green-50 font-semibold">
                          <TableCell colSpan={2} className="capitalize">
                            {subtype}
                          </TableCell>
                        </TableRow>
                        {group.accounts.map((account) => (
                          <TableRow key={account.id}>
                            <TableCell className="pl-8">{account.name}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(account.balance)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="font-semibold border-t">
                          <TableCell className="pl-4 capitalize">
                            Total {subtype}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(group.subtotal)}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ),
                  )}

                  {/* Total Assets */}
                  <TableRow className="bg-green-100 font-bold text-lg border-t-2">
                    <TableCell>TOTAL ASSETS</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(statement.assets?.total)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Liabilities & Equity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-red-700">
                LIABILITIES & EQUITY
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableBody>
                  {/* Liabilities */}
                  {Object.entries(statement.liabilities?.accounts || {}).map(
                    ([subtype, group]) => (
                      <React.Fragment key={subtype}>
                        <TableRow className="bg-red-50 font-semibold">
                          <TableCell colSpan={2} className="capitalize">
                            {subtype}
                          </TableCell>
                        </TableRow>
                        {group.accounts.map((account) => (
                          <TableRow key={account.id}>
                            <TableCell className="pl-8">{account.name}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(account.balance)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="font-semibold border-t">
                          <TableCell className="pl-4 capitalize">
                            Total {subtype}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(group.subtotal)}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ),
                  )}

                  {/* Total Liabilities */}
                  <TableRow className="font-bold border-t">
                    <TableCell>Total Liabilities</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(statement.liabilities?.total)}
                    </TableCell>
                  </TableRow>

                  {/* Equity */}
                  {Object.entries(statement.equity?.accounts || {}).map(
                    ([subtype, group]) => (
                      <React.Fragment key={subtype}>
                        <TableRow className="bg-blue-50 font-semibold">
                          <TableCell colSpan={2} className="capitalize">
                            {subtype}
                          </TableCell>
                        </TableRow>
                        {group.accounts.map((account) => (
                          <TableRow key={account.id}>
                            <TableCell className="pl-8">{account.name}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(account.balance)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ),
                  )}
                  <TableRow className="font-semibold border-t">
                    <TableCell className="pl-4">Total Equity</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(statement.equity?.total)}
                    </TableCell>
                  </TableRow>

                  {/* Total Liabilities & Equity */}
                  <TableRow className="bg-red-100 font-bold text-lg border-t-2">
                    <TableCell>TOTAL LIABILITIES & EQUITY</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(
                        statement.liabilities?.total + statement.equity?.total,
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            Select a date and click "Generate Statement" to view the balance
            sheet.
          </CardContent>
        </Card>
      )}

      {/* Balance Sheet Equation */}
      {statement && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center gap-4 text-lg">
              <div className="text-center">
                <p className="text-sm text-gray-600">Assets</p>
                <p className="font-bold text-green-700">
                  {formatCurrency(statement.assets?.total)}
                </p>
              </div>
              <span className="text-2xl font-bold">=</span>
              <div className="text-center">
                <p className="text-sm text-gray-600">Liabilities</p>
                <p className="font-bold text-red-700">
                  {formatCurrency(statement.liabilities?.total)}
                </p>
              </div>
              <span className="text-2xl font-bold">+</span>
              <div className="text-center">
                <p className="text-sm text-gray-600">Equity</p>
                <p className="font-bold text-blue-700">
                  {formatCurrency(statement.equity?.total)}
                </p>
              </div>
              {isBalanced() && (
                <span className="text-3xl text-green-600">✓</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BalanceSheet;
